import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import DescriptionIcon from '@material-ui/icons/Description';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import HomeWorkOutlinedIcon from '@material-ui/icons/HomeWorkOutlined';
import ReportIcon from '@material-ui/icons/Report';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import TranslatedText from 'components/atoms/TranslatedText';
import { PageTypeEnum } from 'constants/pageType';
import PermissionsEnum from 'constants/permissions';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Route, Switch } from 'react-router-dom'; // import the react-router-dom components
import { useHostDetails } from 'redux/reducers/gl_host_provider/selectors';
import { IStoreUrlOutput } from 'services/api/store';
import { userHasAccess } from 'services/local/permissions';
import NavigationConfig, { Appbars } from '.';
import { RequireAuth, WithNavbar } from '../../middlewares';
import { HomeIcon } from 'components/atoms/Icons/HomeIcon';

interface IProps {
  routes: Routes[];
}

interface Routes {
  exact: boolean;
  path: string;
  component: any;
  pageType?: PageTypeEnum;
  requiredLogin?: boolean;
  id?: string;
}

const StackNavigator = ({ routes }: IProps) => {
  // console.log('Stack naviator...');
  return (
    <Switch>
      {routes.map((route, i) => (
        <Route
          key={i}
          exact={route.exact}
          path={route.path}
          render={() => (
            <RequireAuth
              Component={route.component}
              id={route.id}
              requiredLogin={route.requiredLogin}
              pageType={route.pageType}
            />
          )}
        />
      ))}
    </Switch>
  );
};

export const MainRoutes = () => {
  return (
    <main>
      <HelmetTags />
      <Switch>
        {Appbars.map((route, i) => (
          <Route
            key={i}
            exact={false}
            path={'/'}
            render={() => (
              <WithNavbar
                include={route.include}
                exclude={route.exclude}
                select={route.select}
                Component={route.component}
              />
            )}
          />
        ))}
      </Switch>
    </main>
  );
};

const HelmetTags = () => {
  const hostDetails = useHostDetails();
  const hostSettings = hostDetails.data?.settings;
  return (
    <Helmet>
      <meta
        name="description"
        content={hostSettings?.['cellock.metadata.description']}
      />
      <meta
        name="keywords"
        content={hostSettings?.['cellock.metadata.keywords']}
      />
      <title>{hostSettings?.['cellock.metadata.title']}</title>
    </Helmet>
  );
};

/** CHANGE THIS TO PUT NEW ROUTES */
export const Routes = React.memo(() => {
  return (
    <main>
      {/* <AppFooter> */}
      <StackNavigator
        routes={[
          { ...NavigationConfig.homePage(), exact: true },
          { ...NavigationConfig.productsPage(), exact: false },
          { ...NavigationConfig.showBlogPage(), exact: true },
          { ...NavigationConfig.showNewsPage(), exact: true },
          { ...NavigationConfig.showBlogDetailPage(), exact: true },
          { ...NavigationConfig.showNewsDetailPage(), exact: true },
          { ...NavigationConfig.showTopicDetailPage(), exact: true },
          { ...NavigationConfig.productPrintPdf(), exact: true },
          { ...NavigationConfig.productEditPage(), exact: false }, //subroute
          {
            ...NavigationConfig.clientListingPage(),
            exact: true,
            requiredLogin: true,
          },

          { ...NavigationConfig.storesPage(), exact: true },

          { ...NavigationConfig.tasksPage(), exact: false }, //subroute
          { ...NavigationConfig.singleClientPage(), exact: false }, //subroute
          // { ...NavigationConfig.clientAccountPage(), exact: false }, //subroute
          { ...NavigationConfig.categoryEditPage(), exact: true },
          { ...NavigationConfig.specificationAttributeEditPage(), exact: true },
          { ...NavigationConfig.newEditPage(), exact: true },
          { ...NavigationConfig.topicEditPage(), exact: true },
          { ...NavigationConfig.blogEditPage(), exact: true },
          { ...NavigationConfig.storesEditPage(), exact: true },
          { ...NavigationConfig.documentListPage(), exact: true },
          { ...NavigationConfig.documentEditPage(), exact: true },
          { ...NavigationConfig.reportingListPage(), exact: true },
          { ...NavigationConfig.dashboardPage(), exact: true },
          { ...NavigationConfig.accountSettingPage(), exact: false },

          { ...NavigationConfig.securityPage(), exact: true },
          { ...NavigationConfig.productDetailsPage(), exact: true },
          { ...NavigationConfig.chatPage(), exact: true },
          { ...NavigationConfig.gameScenePage(), exact: true },

          // { ...NavigationConfig.loginPage(), exact: true },

          { ...NavigationConfig.mainPage(), exact: true },

          { ...NavigationConfig.forgotPasswordPage(), exact: true },

          { ...NavigationConfig.resetPasswordPage(), exact: true },
          { ...NavigationConfig.mobileEntrypointPage(), exact: true },

          { ...NavigationConfig.clientAccountProfilePage(), exact: true },
          { ...NavigationConfig.clientAccountFavoritesPage(), exact: true },
          { ...NavigationConfig.clientAccountAppointmentsPage(), exact: true },
          { ...NavigationConfig.clientAccountSavedSearchesPage(), exact: true },
          {
            ...NavigationConfig.clientAccountRecommendationPage(),
            exact: true,
          },

          { ...NavigationConfig.rootPage(), exact: true }, // always last
        ]}
      />
      {/* </AppFooter> */}
    </main>
  );
});

export const ClientAccountsRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[
        { ...NavigationConfig.clientAccountProfilePage(), exact: true },
        { ...NavigationConfig.clientAccountFavoritesPage(), exact: true },
        { ...NavigationConfig.clientAccountAppointmentsPage(), exact: true },
        { ...NavigationConfig.clientAccountSavedSearchesPage(), exact: true },
        { ...NavigationConfig.clientAccountRecommendationPage(), exact: true },
      ]}
    />
  );
});

export const PropertyItemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[
        { ...NavigationConfig.productEditBasicDetailsPage(), exact: true },
        { ...NavigationConfig.productEditImagesPage(), exact: true },
        { ...NavigationConfig.productEditDocumentsPage(), exact: true },
        { ...NavigationConfig.productEditNotesPage(), exact: true },
      ]}
    />
  );
});

export const AccountSettingsItemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[
        { ...NavigationConfig.settingsPage(), exact: false },
        { ...NavigationConfig.topicsPage(), exact: true },
        { ...NavigationConfig.blogsPage(), exact: true },
        { ...NavigationConfig.settingsGeneralPage(), exact: true },
        { ...NavigationConfig.newsListPage(), exact: true },
      ]}
    />
  );
});

export const TaskItemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[
        { ...NavigationConfig.tasksList(), exact: true },
        { ...NavigationConfig.tasksCalendarPage(), exact: true },
        { ...NavigationConfig.tasksMapPage(), exact: true },
        { ...NavigationConfig.taskBoardPage(), exact: true },
      ]}
    />
  );
});

export const SingleClientRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[
        { ...NavigationConfig.singleClientClientDetailsPage(), exact: true },
        {
          ...NavigationConfig.singleClientPropertyCollectionsPage(),
          exact: true,
        },
        {
          ...NavigationConfig.singleClientSavedSearchPage(),
          exact: true,
        },
        {
          ...NavigationConfig.singleClientLocationPage(),
          exact: true,
        },
        {
          ...NavigationConfig.singleClientPropertyCollectionsItemPage(),
          exact: true,
        },
        { ...NavigationConfig.singleClientDocumentsPage(), exact: true },
        { ...NavigationConfig.singleClientInternalNotesPage(), exact: true },
        { ...NavigationConfig.singleClientPublicNotesPage(), exact: true },
        { ...NavigationConfig.singleClientRequestPage(), exact: true },
        { ...NavigationConfig.singleClientTaskPage(), exact: true },
        { ...NavigationConfig.singleClientConversationsPage(), exact: true },
      ]}
    />
  );
});

export const TopicItemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[{ ...NavigationConfig.topicEditPage(), exact: true }]}
    />
  );
});

export const BlogItlemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[{ ...NavigationConfig.blogEditPage(), exact: true }]}
    />
  );
});

export const StoresItemRoutes = React.memo(() => {
  return (
    <StackNavigator
      routes={[{ ...NavigationConfig.storesEditPage(), exact: true }]}
    />
  );
});
interface ISubItems {
  path: string;
  title: React.ReactNode;
  component: React.FC;
  id: number;
  requiredLogin: boolean | null;
  permissionid: string | null;
  pageType: PageTypeEnum | null;
}
interface ISidebarConfig {
  title: React.ReactNode;
  path: string | null;
  Icon: React.FC;
  id: number;
  component: React.FC | null;
  subItems?: ISubItems[];
  requiredLogin: boolean | null;
  permissionid: string | null;
  pageType: PageTypeEnum | null;
}

export const SideBarConfig: ISidebarConfig[] = [
  // {
  //   title: <TranslatedText defaultText="Dashboard" />,
  //   path: NavigationConfig.dashboardPage().path,
  //   requiredLogin: NavigationConfig.dashboardPage().requiredLogin ?? null,
  //   Icon: () => <DashboardOutlinedIcon />,
  //   id: 0,
  //   component: NavigationConfig.dashboardPage().component,
  //   permissionid: NavigationConfig.dashboardPage().permission ?? null,
  //   pageType: NavigationConfig.dashboardPage().pageType ?? null,
  // },
  {
    title: <TranslatedText defaultText="Corners" />,
    path: NavigationConfig.productsPage().path,
    requiredLogin: NavigationConfig.productsPage().requiredLogin ?? null,
    Icon: () => <HomeOutlinedIcon />,
    id: 1,
    component: NavigationConfig.productsPage().component,
    permissionid: NavigationConfig.productsPage().permission ?? null,
    pageType: NavigationConfig.productsPage().pageType ?? null,
  },
  {
    title: <TranslatedText defaultText="Users" />,
    path: NavigationConfig.clientListingPage().path,
    requiredLogin: NavigationConfig.clientListingPage().requiredLogin ?? null,
    Icon: () => <AccountCircleOutlinedIcon />,
    id: 2,
    component: NavigationConfig.clientListingPage().component,
    permissionid: NavigationConfig.clientListingPage().permission ?? null,
    pageType: NavigationConfig.clientListingPage().pageType ?? null,
  },

  {
    title: <TranslatedText defaultText="Stores" />,
    path: NavigationConfig.storesPage().path,
    requiredLogin: NavigationConfig.storesPage().requiredLogin ?? null,
    Icon: () => <HomeWorkOutlinedIcon />,
    id: 5,
    component: NavigationConfig.storesPage().component,
    permissionid: NavigationConfig.storesPage().permission ?? null,
    pageType: NavigationConfig.storesPage().pageType ?? null,
  },

  {
    title: <TranslatedText defaultText="Tasks" />,
    path: NavigationConfig.tasksPage().path,
    requiredLogin: NavigationConfig.tasksPage().requiredLogin ?? null,
    Icon: () => <AssignmentOutlinedIcon />,
    id: 7,
    component: NavigationConfig.tasksPage().component,
    permissionid: NavigationConfig.tasksPage().permission ?? null,
    pageType: NavigationConfig.tasksPage().pageType ?? null,
  },

  {
    title: <TranslatedText defaultText="Documents" />,
    path: NavigationConfig.documentListPage().path,
    requiredLogin: NavigationConfig.documentListPage().requiredLogin ?? null,
    Icon: () => <DescriptionIcon />,
    id: 17,
    component: NavigationConfig.documentListPage().component,
    permissionid: NavigationConfig.documentListPage().permission ?? null,
    pageType: NavigationConfig.documentListPage().pageType ?? null,
  },
  {
    title: <TranslatedText defaultText="Reporting" />,
    path: NavigationConfig.reportingListPage().path,
    requiredLogin: NavigationConfig.reportingListPage().requiredLogin ?? null,
    Icon: () => <ReportIcon />,
    id: 24,
    component: NavigationConfig.reportingListPage().component,
    permissionid: NavigationConfig.reportingListPage().permission ?? null,
    pageType: NavigationConfig.reportingListPage().pageType ?? null,
  },
  {
    title: <TranslatedText defaultText="Account Settings" />,
    path: NavigationConfig.accountSettingPage().path,
    requiredLogin: NavigationConfig.accountSettingPage().requiredLogin ?? null,
    Icon: () => <SettingsOutlinedIcon />,
    id: 25,
    component: NavigationConfig.accountSettingPage().component,
    permissionid: NavigationConfig.accountSettingPage().permission ?? null,
    pageType: NavigationConfig.accountSettingPage().pageType ?? null,
  },
];

function filterSubElements(
  subElements: ISubItems[],
  isLogin: boolean,
  userPermissions:
    | {
        [key in PermissionsEnum]: string;
      }
    | null,
  settingsMap: IStoreUrlOutput['data']['settings'] | null
): ISubItems[] {
  return (
    subElements.filter(({ requiredLogin, permissionid, pageType }) =>
      userHasAccess({
        requiredLogin,
        requiredPermission: (permissionid as any) || undefined,
        isLogin,
        userPermissionsMap: userPermissions || null,
        pageType,
        settingsMap,
      })
    ) || []
  );
}

// export const getNavbarConfig = (
//   roles: Array<Roles> | null,
//   userPermissions:
//     | {
//         [key in PermissionsEnum]: string;
//       }
//     | null
// ): ISidebarConfig[] => {
//   return (
//     Array.isArray(roles)
//       ? SideBarConfig.filter(({ requiredRoles, permissionid }) =>
//           userHasAccess({
//             requiredRoles: (requiredRoles as Roles[]) || undefined,
//             requiredPermission: (permissionid as any) || undefined,
//             userRoles: roles,
//             userPermissionsMap: userPermissions || undefined,
//           })
//         )
//       : []
//   ).map((element) => ({
//     ...element,
//     subItems: Array.isArray(element.subItems)
//       ? filterSubElements(element.subItems, roles || [], userPermissions)
//       : element.subItems,
//   }));
// };

export const getNavbarConfig = (
  isLogin: boolean,
  userPermissions:
    | {
        [key in PermissionsEnum]: string;
      }
    | null,
  settingsMap: IStoreUrlOutput['data']['settings'] | null
): ISidebarConfig[] => {
  return SideBarConfig.filter(({ requiredLogin, permissionid, pageType }) =>
    userHasAccess({
      requiredLogin: requiredLogin ?? null,
      requiredPermission: (permissionid as any) || null,
      isLogin,
      userPermissionsMap: userPermissions || null,
      settingsMap: settingsMap ?? null,
      pageType,
    })
  ).map((element) => ({
    ...element,
    subItems: Array.isArray(element.subItems)
      ? filterSubElements(
          element.subItems,
          isLogin,
          userPermissions,
          settingsMap
        )
      : element.subItems,
    settingsMap,
  }));
};
